<script setup>
// Astra DS · AstraWord — el logotipo, la palabra ASTRA entera.
//
// Figma: `AstraWord · oblicua 1691:101`, exportado el 08.09.26 desde la hoja
// `Exportables · A oblicua 1697:93`. Cinco letras, un vector cada una, **pegadas
// tal cual**: no se redibuja ni se ajusta a ojo.
//
// Entra en la cabecera de escritorio el 08.09.26 y sustituye ahí a la A, que se
// queda para todo lo pequeño: móvil, favicon, icono de aplicación y avatar.
//
// ── Por qué no lleva token de acento ──
//
// ⚠️ **El logotipo tiene color propio y es degradado**, así que no cuelga de
// `icon/accent` como colgaba la marca. Y las paradas **cambian con el modo**:
// en Figma no se pueden tokenizar —las paradas de un degradado no admiten
// variable— así que el archivo solo lleva las de oscuro y las de claro se
// resuelven aquí. De ahí los tres tokens `brand/word-*` de `tokens.scss`, que
// son la única forma de que el degradado siga al tema.
//
// ⚠️ **Suelo de uso: 16 px de alto.** Por debajo se pierden los cortes en
// diagonal de los remates. En la cabecera va a 18.
//
// ⚠️ **No se le añade el «FM»**: con la palabra sola en la celda no aparece por
// ninguna parte, y está sin decidir. No lo pongas por tu cuenta.
import { useId } from "vue";

defineProps({
  // Alto en píxeles. El ancho sale de la proporción del vector —5,558 a 1— y
  // nunca se fija: fijar los dos deforma la palabra.
  alto: { type: [Number, String], default: 18 },
});

// Un id por instancia, de `useId` (Vue 3.5). Dos logotipos en la misma página
// compartiendo el id del degradado funcionan hasta que el primero se desmonta:
// el segundo se queda apuntando a un `defs` que ya no existe y pierde el color.
// Un contador declarado aquí no serviría — `script setup` corre una vez por
// instancia, así que valdría 1 en todas.
const id = `astra-word-grad-${useId()}`;
</script>

<template>
  <svg
    class="astra-word"
    viewBox="0 0 1158.89 208.5"
    :height="alto"
    role="img"
    aria-label="Astra FM"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <defs>
      <!-- La misma inclinación que tenía el campo Rescoldo: de arriba a la
           izquierda a abajo a la derecha. Coordenadas del propio export. -->
      <linearGradient
        :id="id"
        x1="222.001"
        y1="40.3125"
        x2="267.002"
        y2="290.984"
        gradientUnits="userSpaceOnUse"
      >
        <stop stop-color="var(--brasa-1)" />
        <stop offset="0.52" stop-color="var(--brasa-2)" />
        <stop offset="1" stop-color="var(--brasa-3)" />
      </linearGradient>
    </defs>
    <path d="M1.79856 207C-0.201441 207 -0.53144 205.67 0.798562 203L122.799 5C124.799 1.67 127.129 0 129.799 0H163.799C167.129 0 169.469 1.67 170.799 5L281.799 202C283.799 205.33 283.469 207 280.799 207H233.799C230.469 207 228.129 205.67 226.799 203L145.799 59L105.298 126L179.799 143C182.469 143.67 184.469 145.33 185.799 148L195.799 177C197.129 181 195.799 182.67 191.799 182L89.2978 156L57.7986 202C55.7986 205.33 53.4686 207 50.7986 207H1.79856Z" :fill="`url(#${id})`" />
    <path d="M454.599 1.5C457.929 1.5 458.929 3.17 457.599 6.5L442.599 41.5C441.929 44.17 439.929 45.5 436.599 45.5H326.598C320.598 45.5 319.598 47.83 323.598 52.5L436.599 118.5C449.269 125.83 455.599 135.17 455.599 146.5C455.599 152.5 453.929 159.17 450.599 166.5L430.599 203.5C428.599 206.83 425.929 208.5 422.599 208.5H299.598C296.928 208.5 294.928 206.83 293.598 203.5L286.598 172.5C285.928 168.5 287.268 166.5 290.598 166.5H395.599C402.269 166.5 402.929 163.83 397.599 158.5L288.598 96.5C274.598 88.5 267.598 79.17 267.598 68.5C267.598 59.17 270.598 50.83 276.598 43.5L310.598 6.5C313.268 3.17 316.268 1.5 319.598 1.5H454.599Z" :fill="`url(#${id})`" />
    <path d="M476.599 1.5H657.6C661.6 1.5 662.6 3.5 660.6 7.5L645.6 40.5C644.265 43.83 641.935 45.5 638.6 45.5H579.6V204.5C579.6 207.17 578.265 208.5 575.6 208.5H536.6C533.935 208.5 532.6 207.17 532.6 204.5V45.5H463.599C460.264 45.5 459.599 43.5 461.599 39.5L476.599 1.5Z" :fill="`url(#${id})`" />
    <path d="M694.599 1.5H831.6C834.27 1.5 836.6 2.83 838.6 5.5L860.6 38.5C865.27 45.17 867.6 53.5 867.6 63.5C867.6 73.5 863.93 81.5 856.6 87.5L808.6 126.5L866.6 202.5C869.93 206.5 869.27 208.5 864.6 208.5H814.6C811.27 208.5 808.6 206.83 806.6 203.5L751.599 138.5C746.269 131.83 746.269 125.83 751.599 120.5L811.6 67.5C818.27 61.5 818.27 54.5 811.6 46.5L726.599 45.5V204.5C726.599 207.17 725.269 208.5 722.599 208.5H679.599C676.929 208.5 675.599 207.17 675.599 204.5V45.5L694.599 1.5Z" :fill="`url(#${id})`" />
    <path d="M877.598 208.5C875.598 208.5 875.268 207.17 876.598 204.5L998.599 6.5C1000.6 3.17 1002.93 1.5 1005.6 1.5H1039.6C1042.93 1.5 1045.27 3.17 1046.6 6.5L1157.6 203.5C1159.6 206.83 1159.27 208.5 1156.6 208.5H1109.6C1106.27 208.5 1103.93 207.17 1102.6 204.5L1021.6 60.5L981.798 126.5L1055.6 144.5C1058.27 145.17 1060.27 146.83 1061.6 149.5L1071.6 178.5C1072.93 182.5 1071.6 184.17 1067.6 183.5L963.798 158L933.598 203.5C931.598 206.83 929.268 208.5 926.598 208.5H877.598Z" :fill="`url(#${id})`" />
  </svg>
</template>

<style scoped lang="scss">
// ── El degradado, aquí y en hex ──
//
// 🔴 **No son tokens, y es una decisión, no un descuido.** Pablo cerró el
// 08.09.26 que las tres paradas viven en el componente: el logotipo lleva color
// propio —ya no cuelga de `icon/accent`— y un token de sistema para un
// degradado que solo usa él confunde más de lo que ordena. Está escrito en
// DESIGN.md § El degradado se queda en el código.
//
// ⚠️ **Voltean con el modo.** En Figma las paradas de un degradado no admiten
// variable, así que el archivo solo puede llevar unas —las de oscuro— y las de
// claro se resuelven aquí. Sobre papel el degradado tiene que empezar por el
// extremo oscuro o se lava.
//
// ⚠️ `#b72a25` sobre el lienzo oscuro da **3,08** de contraste, y el mínimo
// para un elemento gráfico es 3,00: está dentro, pero sin margen.
// **No oscurecer más ese extremo** — `#a02620` cae a 2,55.
.astra-word {
  --brasa-1: #cc5238;
  --brasa-2: #c2402c;
  --brasa-3: #b72a25;

  display: block;
  flex: none;
  // Ver AstraIcon: la capa legacy estira todo `svg` al 100% y un selector de
  // etiqueta gana al atributo. Sin esto la palabra ocupa el ancho del padre y
  // se deforma.
  width: auto;
  height: auto;
}

// El juego de claro. Sin `:global()`: Vue aplica el scope al ÚLTIMO selector,
// así que esto compila a `html[data-theme=light] .astra-word[data-v-…]` y casa. Con
// `:global()` no llegaba a generarse la regla — comprobado en el navegador el
// 09.09.26, el degradado se quedaba en las paradas de oscuro sobre papel.
html[data-theme="light"] .astra-word {
  --brasa-1: #b72a25;
  --brasa-2: #c14434;
  --brasa-3: #c95a44;
}
</style>
